import React, { useState, useEffect } from 'react'
import ReferenceSheetProvider, {
  useReferenceSheet,
} from './ReferenceSheetProvider'
import { Textarea } from '../../lib/ui/textarea'
import { Button } from '../../lib/ui/button'
import { TemplateProvider, useTemplate } from '~/lib/pdf'
import {
  TabNav,
  TabPanel,
  PanelContainer,
  ButtonContainer,
  TabButton,
  setTabNavActiveTab,
} from '~/components/shared/TabNav'
import {
  DocumentMagnifyingGlassIcon,
  IdentificationIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import Editor from './Editor/index'
import PdfViewerPanel from './PdfViewerPanel'
import useTranslation, {
  TranslationProvider,
} from '~/components/shared/TranslationProvider'
import translations from './locales.json'
import { templates, templateStyle, templateSupportedStyles } from './templates'

const ReferenceSheetBuilder = ({
  locale,
  template_settings,
  template_index,
  ...props
}) => {
  const defaultStyle = template_settings
    ? {
        ...templateStyle,
        [`template-${template_index}`]: template_settings,
      }
    : templateStyle
  const templateProps = {
    templates,
    defaultStyle,
    templateSupportedStyles,
    template_index,
    dev_mode: props.dev_mode,
  }
  return (
    <TranslationProvider
      locale={locale}
      translations={translations}
      scope="javascript.components.ReferenceSheetBuilder"
    >
      <TemplateProvider {...templateProps}>
        <ReferenceSheetProvider locale={locale} {...props}>
          <ReferenceSheetBuilderInner />
        </ReferenceSheetProvider>
      </TemplateProvider>
    </TranslationProvider>
  )
}
export default ReferenceSheetBuilder

const ReferenceSheetBuilderInner = () => {
  const { t } = useTranslation('')
  const { devMode, documentId, previewTemplateIndex, setPreviewTemplateIndex } =
    useReferenceSheet()
  const { templateIndex } = useTemplate()

  const defaultActiveTab = documentId ? 'details' : 'chooseTemplate'

  const onChangeActiveTab = (name) => {
    if (name == 'chooseTemplate') {
      setPreviewTemplateIndex(templateIndex)
    } else {
      setPreviewTemplateIndex(null)
    }
  }

  return (
    <div className="flex w-full flex-col md:flex-row">
      <div className="md:basis-3/5">
        <TabNav
          className="px-4 pt-4 lg:px-8 xl:px-12"
          defaultActiveTab={defaultActiveTab}
          onChangeActiveTab={onChangeActiveTab}
          mobileMode="swipe"
        >
          <ButtonContainer>
            <TabButton
              name="chooseTemplate"
              text={t('choose_template')}
              Icon={DocumentMagnifyingGlassIcon}
            />
            <TabButton
              name="details"
              text={t('details')}
              Icon={IdentificationIcon}
            />
            <TabButton
              name="review"
              text={t('review')}
              Icon={ClipboardDocumentCheckIcon}
            />
            {devMode && (
              <TabButton
                name="text"
                text={t('raw_json')}
                Icon={DocumentTextIcon}
              />
            )}
          </ButtonContainer>
          <PanelContainer className="md:min-h-[130vh]">
            <TabPanel name="chooseTemplate" className="bg-[#E0F2F9] pt-8">
              <SelectTemplatePanel />
            </TabPanel>
            <TabPanel name="details" className="pt-8">
              <Editor />
            </TabPanel>
            <TabPanel name="review" className="pt-8">
              <ReviewPanel />
            </TabPanel>
            <TabPanel name="text" className="pt-8">
              <TextEditor />
            </TabPanel>
          </PanelContainer>
        </TabNav>
      </div>
      <div id="resume-pdf-preview" className="relative md:flex-grow">
        <div className="h-screen w-full md:sticky md:left-0 md:top-0">
          {previewTemplateIndex === null ? (
            <PdfViewerPanel />
          ) : (
            <RightPanelTemplatePreview />
          )}
        </div>
      </div>
    </div>
  )
}

const SelectTemplatePanel = () => {
  const { t } = useTranslation('')
  const { previewTemplateIndex, setPreviewTemplateIndex } = useReferenceSheet()
  const activeClasses = 'font-roboto-slab text-primary font-bold pb-2'
  const inactiveClasses = 'font-roboto-slab text-primary pb-2'

  return (
    <div className="grid h-full sm:grid-cols-2 lg:grid-cols-3">
      {templates.map((_, index) => (
        <div className="flex flex-col items-center" key={`template-${index}`}>
          <span
            className={
              previewTemplateIndex == index ? activeClasses : inactiveClasses
            }
          >
            {t('style_index', { num: index + 1 })}
          </span>
          <button
            onClick={() => setPreviewTemplateIndex(index)}
            disabled={previewTemplateIndex === index}
            className={
              previewTemplateIndex === index
                ? 'rounded-xl border-4 border-solid border-primary'
                : 'm-1 rounded-xl'
            }
          >
            <TemplatePreview
              className="w-48 rounded-xl"
              templateIndex={index}
            />
          </button>
        </div>
      ))}
    </div>
  )
}

const RightPanelTemplatePreview = () => {
  const { t } = useTranslation()
  const { previewTemplateIndex } = useReferenceSheet()
  const { setTemplateIndex } = useTemplate()
  const selectTemplateClick = () => {
    setTemplateIndex(previewTemplateIndex)
    setTabNavActiveTab('details')
  }

  return (
    <div className="flex flex-col bg-[#dadfe3] px-6 pt-8 md:h-full md:max-w-[35vw]">
      <div className="mb-6 flex flex-row items-center justify-around">
        <Button variant="default" onClick={selectTemplateClick}>
          {t('select_template')}
        </Button>
      </div>
      <TemplatePreview
        templateIndex={previewTemplateIndex}
        className="rounded-lg"
      />
    </div>
  )
}

const TemplatePreview = ({ className, templateIndex }) => {
  const { t } = useTranslation('')
  const { locale } = useReferenceSheet()
  const localizedPath = locale == 'en' ? '' : `/${locale}`

  const url = `https://www-yns-com.s3.amazonaws.com/resume_builder${localizedPath}/ReferenceSheetTemplate${templateIndex}.png`
  return (
    <img
      className={className}
      src={url}
      alt={t('template_index', { num: templateIndex })}
    />
  )
}

const ReviewPanel = () => {
  const { t } = useTranslation('')
  const { persistReferenceSheet, documentId } = useReferenceSheet()
  const referenceSheetIsNew = !documentId

  return (
    <div className="flex flex-col">
      <div className="flex flex-row justify-evenly">
        <Button onClick={persistReferenceSheet}>
          {referenceSheetIsNew ? t('create') : t('save')}
        </Button>
      </div>
    </div>
  )
}

const TextEditor = () => {
  const { t } = useTranslation('')
  const { referenceSheetDocument, setReferenceSheetDocument } =
    useReferenceSheet()
  const [editorReferenceSheet, setEditorReferenceSheet] = useState(
    JSON.stringify(referenceSheetDocument, null, 2),
  )

  const sync = () => {
    const updatedReferenceSheet = JSON.parse(editorReferenceSheet)
    setReferenceSheetDocument(updatedReferenceSheet)
  }

  useEffect(() => {
    setEditorReferenceSheet(JSON.stringify(referenceSheetDocument, null, 2))
  }, [referenceSheetDocument])

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-row justify-evenly">
        <Button onClick={sync}>{t('sync')}</Button>
      </div>
      <Textarea
        className="h-full w-full"
        value={editorReferenceSheet}
        onChange={(e) => setEditorReferenceSheet(e.target.value)}
      ></Textarea>
    </div>
  )
}
